import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Button, Paper, TextField, Typography, Dialog, 
    DialogTitle, DialogContent, DialogActions, 
    Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, 
    IconButton, MenuItem, Select, FormControl, 
    InputLabel, Tooltip 
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { SortableItem } from '../Common/SortableItem';
import { Visibility, ExpandMore, Delete, Add } from '@mui/icons-material';
import { FieldEditor } from './FieldEditor';
import { FormFieldPreview } from './FormFieldPreview';
import { fieldTypes, formTemplates } from '../Common/FormFieldTypes';
import { APIApplicationFormUpdate } from '../../utilities/APIs';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { useAuth } from '../../users/AuthContext';
import { getLoggedInUserName } from '../../utilities/Functions';
import useCustomToast from '../../hooks/useToast';

export default function FormEditBuilder({ formData, onCancel }) {
    const { makeAuthenticatedRequest } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [formType, setFormType] = useState('undergraduate');
    const [formSections, setFormSections] = useState(formTemplates.undergraduate);
    const [activeSection, setActiveSection] = useState(formTemplates.undergraduate[0].id);
    const [formName, setFormName] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [sectionToDelete, setSectionToDelete] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formId, setFormId] = useState(null);
    const showToast = useCustomToast()
    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

    // Initialize form data when component mounts or formData changes
    // Also, ensure default fields are preserved when editing:
    useEffect(() => {
        if (formData) {
            const config = JSON.parse(formData.formConfigJson);
            setFormId(formData.id);
            setFormName(config.name);
            setFormType(config.type);
            
            // Merge existing fields with default fields, preserving any customizations
            const mergedSections = config.sections.map((section, index) => {
                if (index === 0) { 
                    // Only merge for the first section (BASIC INFORMATION)
                    const defaultFields = formTemplates[config.type][0].fields;
                    
                    // Create a map of existing fields by id for quick lookup
                    const existingFieldsMap = new Map(
                        section.fields.map(field => [field.id, field])
                    );
                    
                    // Merge default fields with existing fields
                    const mergedFields = defaultFields.map(defaultField => {
                        const existingField = existingFieldsMap.get(defaultField.id);
                        if (existingField) {
                            // Preserve existing field properties but keep it locked
                            return { 
                                ...existingField, 
                                locked: true,
                                type: defaultField.type // Ensure type matches default
                            };
                        }
                        return defaultField;
                    });
                    
                    // Add any additional fields that weren't in defaults
                    const additionalFields = section.fields.filter(
                        field => !defaultFields.some(df => df.id === field.id)
                    );
                    
                    return {
                        ...section,
                        fields: [...mergedFields, ...additionalFields]
                    };
                }
                return section;
            });
            
            setFormSections(mergedSections);
            setActiveSection(mergedSections[0]?.id || 'section1');
            setIsEditing(true);
        } else {
            // For new forms, use the template with default fields
            setFormId(null);
            setFormName('');
            setFormType('undergraduate');
            setFormSections(formTemplates.undergraduate);
            setActiveSection(formTemplates.undergraduate[0].id);
            setIsEditing(false);
        }
    }, [formData]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFormTypeChange = (event) => {
        const newFormType = event.target.value;
        setFormType(newFormType);
        setFormSections(formTemplates[newFormType]);
        setActiveSection(formTemplates[newFormType][0].id);
    };

    const handleSectionChange = (sectionId) => {
        setActiveSection(sectionId);
    };

    const getActiveSectionFields = () => {
        const section = formSections.find(s => s.id === activeSection);
        return section ? section.fields : [];
    };

    const setActiveSectionFields = (fields) => {
        setFormSections(prev => prev.map(section => 
            section.id === activeSection ? { ...section, fields } : section
        ));
    };


    const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const activeField = getActiveSectionFields().find(f => f.id === active.id);
    const overField = getActiveSectionFields().find(f => f.id === over.id);
    
    // Prevent moving locked fields or moving items before locked fields
    if (activeField?.locked || overField?.locked) {
        return;
    }
    
    const currentFields = getActiveSectionFields();
    const activeIndex = currentFields.findIndex(item => item.id === active.id);
    const overIndex = currentFields.findIndex(item => item.id === over.id);
    
    // Find the first locked field index
    const firstLockedIndex = currentFields.findIndex(field => field.locked);
    
    // Prevent moving items before locked fields
    if (firstLockedIndex >= 0 && overIndex < firstLockedIndex) {
        return;
    }
    
    const updatedFields = arrayMove(currentFields, activeIndex, overIndex);
    setActiveSectionFields(updatedFields);
};

    const addField = (type) => {
        const newField = {
            id: `field-${Date.now()}`,
            type,
            label: `${type} Field`,
            required: true,
            options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : undefined,
            groupName: type === 'checkbox' ? `group-${Date.now()}` : undefined,
            value: type === 'checkbox' ? false : undefined,
            checked: false
        };
        setActiveSectionFields([...getActiveSectionFields(), newField]);
    };

    const updateField = (id, updates) => {
        setFormSections(prevSections => {
            return prevSections.map(section => {
                if (section.id === activeSection) {
                    const currentField = section.fields.find(f => f.id === id);
                    
                    // Handle checkbox groups
                    if (currentField?.type === 'checkbox' && 'checked' in updates) {
                        return {
                            ...section,
                            fields: section.fields.map(field => {
                                // If this is the checkbox being updated
                                if (field.id === id) {
                                    return { ...field, ...updates };
                                }
                                // If this is another checkbox in the same group
                                if (field.type === 'checkbox' && 
                                    field.groupName === currentField.groupName &&
                                    field.groupName) {
                                    return { ...field, checked: false };
                                }
                                return field;
                            })
                        };
                    }
                    
                    // Handle all other updates
                    return {
                        ...section,
                        fields: section.fields.map(field => 
                            field.id === id ? { ...field, ...updates } : field
                        )
                    };
                }
                return section;
            });
        });
    };


    const removeField = (id) => {
    const fieldToRemove = getActiveSectionFields().find(field => field.id === id);
    if (fieldToRemove?.locked) {
        showToast('Warning', 'This field cannot be deleted as it is required for all forms', 'warning');
        return;
    }
    
    setFormSections(prevSections => 
        prevSections.map(section => {
            if (section.id === activeSection) {
                return {
                    ...section,
                    fields: section.fields.filter(field => field.id !== id)
                };
            }
            return section;
        })
    );
};

    const addSection = () => {
        const newSection = {
            id: `section-${Date.now()}`,
            name: 'New Section',
            fields: []
        };
        setFormSections([...formSections, newSection]);
        setActiveSection(newSection.id);
    };

    const updateSection = (id, updates) => {
        setFormSections(prev => prev.map(section => 
            section.id === id ? { ...section, ...updates } : section
        ));
    };

    const confirmDeleteSection = (id) => {
        setSectionToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const removeSection = (id) => {
        if (formSections.length <= 1) {
            alert('You must have at least one section');
            return;
        }
        setFormSections(formSections.filter(section => section.id !== id));
        if (activeSection === id) {
            setActiveSection(formSections[0].id);
        }
        setDeleteConfirmOpen(false);
    };

    const saveForm = async () => {
        if (!formName.trim()) {
            alert('Please enter a form name');
            return;
        }

        const formConfig = {
            type: formType,
            name: formName,
            sections: formSections.map(section => ({
                id: section.id,
                name: section.name,                fields: section.fields.map((field, index) => ({
                    id: field.id,
                    type: field.type,
                    label: field.label,
                    required: field.required,
                    options: field.options,
                    groupName: field.groupName,
                    skipLogic: field.skipLogic,
                    order: index,
                    // File upload configuration properties
                    allowedFileTypes: field.allowedFileTypes,
                    maxFileSize: field.maxFileSize,
                    allowMultiple: field.allowMultiple
                }))
            }))
        };        const formData = new FormData();
        const createdBy = getLoggedInUserName();
        
        // Only append createdBy if we have a valid value, otherwise let the server handle it
        if (createdBy && createdBy !== "DefaultUser") {
            formData.append('createdBy', createdBy);
        }
        formData.append('type', formType);
        formData.append('name', formName);
        formData.append('updateId', formId);
        formData.append('formConfigJson', JSON.stringify(formConfig));

        // formData.forEach((value, key) => {
        //     console.log(key, value);        // });

        try {
            let response = await makeAuthenticatedRequest(APIApplicationFormUpdate, {
                method: 'PUT',
                body: formData
            });

            const msg = response.message;
            const status = response.status;
            
            if (status === "success") {
                showToast('Success',msg,'success')
                onCancel();
            }
        }  catch (error) {
                // console.log("Error creating form:", error);
                if (error.message.includes("Network")) {
                    showToast('Error',"Network error: " + TEXT_CONNECTION_ERROR,'error')
                } else if (error.status === 401 || error.message?.includes("Unauthorized")) {
                    showToast('Error',"Authentication error: You must be logged in to create forms. Please log out and log in again.",'error')
                } else {
                    showToast('Error',"An error occurred while creating the form: " + error.message,'error')
                }
            }
    };

    const handlePreviewOpen = () => {
        if (!formName.trim()) {
            showToast('Warning','Please enter a form name before previewing','warning');
            return;
        }
        setPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };

    return (
        <Box p={3} sx={{ overflow: 'auto', height: 'calc(100vh - YOUR_HEADER_HEIGHT_HERE)' }}>
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" gutterBottom>
                    Edit Application Form
                </Typography>
                <Button 
                    variant="contained" 
                    color='secondary'
                    startIcon={<FormatListBulletedIcon />}
                    onClick={onCancel}
                >
                    List All Forms
                </Button>
            </Box>
            <Box sx={{ mb: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Form Type</InputLabel>
                    <Select
                        value={formType}
                        label="Form Type"
                        onChange={handleFormTypeChange}
                        disabled={isEditing} // Disable type change when editing
                    >
                        <MenuItem value="undergraduate">Undergraduate Application</MenuItem>
                        <MenuItem value="graduate">Graduate Masters Application</MenuItem>
                        <MenuItem value="shortcourse">Short Course Application</MenuItem>
                    </Select>
                </FormControl>
                
                <TextField
                    label="Form Name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                />
            </Box>
            
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label="Form Builder" />
                <Tab label="Form Preview" />
            </Tabs>
            
            {activeTab === 0 && (
                <Box sx={{ display: 'flex', gap: 4 }}>
                    {/* Field Palette */}
                    <Paper sx={{ p: 2, width: 250 }}>
                        <Typography variant="h6" gutterBottom>Field Types</Typography>
                        {fieldTypes.map((type) => (
                            <Button 
                                key={type.id} 
                                variant="outlined" 
                                fullWidth 
                                sx={{ mb: 1 }}
                                onClick={() => addField(type.id)}
                            >
                                {type.label}
                            </Button>
                        ))}
                        
                        <Button 
                            variant="contained" 
                            startIcon={<Add />}
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={addSection}
                        >
                            Add Section
                        </Button>
                    </Paper>
                    
                    {/* Form Sections */}
                    <Box sx={{ flexGrow: 1 }}>
                        {formSections.map((section) => (
                            <Accordion 
                                key={section.id} 
                                expanded={activeSection === section.id}
                                onChange={() => handleSectionChange(section.id)}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore />}
                                    onClick={(e) => {
                                        if (e.target.closest('.delete-section-button')) {
                                            e.stopPropagation();
                                            confirmDeleteSection(section.id);
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Typography sx={{ flexGrow: 1 }}>{section.name}</Typography>
                                        <Tooltip title="Delete section">
                                            <IconButton 
                                                className="delete-section-button"
                                                edge="end"
                                                sx={{ ml: 1 }}
                                                component="span"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {activeSection === section.id && (
                                        <>
                                            <TextField
                                                label="Section Name"
                                                value={section.name}
                                                onChange={(e) => updateSection(section.id, { name: e.target.value })}
                                                fullWidth
                                                sx={{ mb: 2 }}
                                            />
                                            
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={closestCenter}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <SortableContext
                                                    items={section.fields}
                                                    strategy={verticalListSortingStrategy}
                                                >                                                    {section.fields.map((field) => (
                                                        <SortableItem key={field.id} id={field.id}>
                                                            <FieldEditor
                                                                field={field}
                                                                onUpdate={updateField}
                                                                onRemove={removeField}
                                                                allFields={section.fields}
                                                                allSections={formSections}
                                                            />
                                                        </SortableItem>
                                                    ))}
                                                </SortableContext>
                                            </DndContext>
                                            
                                            {section.fields.length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                                    Drag fields here or click on field types to add them
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Box>
            )}
            
            {activeTab === 1 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        {formName || 'Untitled Form'} - Preview
                    </Typography>
                    
                    {formSections.map((section) => (
                        <Box key={section.id} sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                {section.name}
                            </Typography>
                            
                            <Paper sx={{ p: 2 }}>
                                {section.fields.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No fields in this section
                                    </Typography>
                                ) : (
                                    section.fields.map((field) => (
                                        <FormFieldPreview 
                                            key={field.id} 
                                            field={field} 
                                            onUpdate={updateField} 
                                        />
                                    ))
                                )}
                            </Paper>
                        </Box>
                    ))}
                </Paper>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                    variant="contained" 
                    color='secondary'
                    startIcon={<FormatListBulletedIcon />}
                    onClick={onCancel}
                >
                    Back to List
                </Button>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<Visibility />}
                        onClick={handlePreviewOpen}
                    >
                        Full Preview
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={saveForm}
                    >
                        {isEditing ? 'Update Form' : 'Save Form'}
                    </Button>
                </Box>
            </Box>
            
            {/* Full Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={handlePreviewClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>{formName || 'Untitled Form'} - Full Preview</DialogTitle>
                <DialogContent dividers>
                    {formSections.map((section) => (
                        <Box key={section.id} sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                {section.name}
                            </Typography>
                            
                            <Paper sx={{ p: 2 }}>
                                {section.fields.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No fields in this section
                                    </Typography>
                                ) : (
                                    section.fields.map((field) => (
                                        <FormFieldPreview 
                                            key={field.id} 
                                            field={field} 
                                            onUpdate={updateField} 
                                        />
                                    ))
                                )}
                            </Paper>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePreviewClose}>Close</Button>
                    <Button variant="contained" onClick={saveForm}>
                        {isEditing ? 'Update Form' : 'Save Form'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Delete Section?</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this section? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            removeSection(sectionToDelete);
                            setDeleteConfirmOpen(false);
                        }}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}